/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  NavDirection,
  addSortingAttributes,
  calcDistanceAngleWeighted,
  calcInContact,
  createNodeBoundsMap,
  getLinkComparator,
  getNavigableLinksForNodeId,
  getNodeDetailFromInfo,
  isValidDestination
} from '../utils/navUtils';
import type { DiagramNode, DiagramLink, Bounds, ItemInfo } from '../Diagram.types';
import { useRef } from 'preact/hooks';

export function useDiagramNavigation<K1, K2>(
  nodes: DiagramNode<K1>[],
  links: DiagramLink<K2, K1>[],
  bounds: Bounds[],
  linkIdToDataMap: Map<K2, DiagramLink<K2, K1>>
) {
  const currentKeyboardFocusNode = useRef<K1>();
  const nodeBoundsById = createNodeBoundsMap(nodes, bounds);

  function getNextNavigableNode(
    direction: NavDirection,
    compareCenters: boolean,
    current?: DiagramNode<K1>,
    listOfObjects?: DiagramNode<K1>[]
  ) {
    if (!listOfObjects) return current;

    if (!current) return listOfObjects[0];
    const currBB = nodeBoundsById.get(current.id);
    if (!currBB) return listOfObjects[0];

    let nextObject = current; //init to current object
    let nextDistance = Number.MAX_VALUE;

    // If an object is in contact it overrules all other attributes
    // Only another in contact object with better attributes will have higher precedence
    let nextInContact = false;

    // Whether or not the for loop has encountered the current object
    let hasFoundCurrent = false;

    let object, inContact, distance;
    for (let i = 0; i < listOfObjects.length; i++) {
      object = listOfObjects[i];

      if (object === current) {
        hasFoundCurrent = true;
        continue;
      }
      const objBB = nodeBoundsById.get(object.id);
      if (!objBB) continue;

      const objBounds = { x: objBB.x, y: objBB.y, w: objBB.w, h: objBB.h };
      const currBounds = { x: currBB.x, y: currBB.y, w: currBB.w, h: currBB.h };
      if (!isValidDestination(objBounds, currBounds, direction, compareCenters)) continue;

      inContact = calcInContact(objBounds, currBounds, direction);

      if (nextInContact && !inContact) continue;

      distance = calcDistanceAngleWeighted(objBounds, currBounds, direction);

      if (distance == 0 && !hasFoundCurrent) continue;

      // Make sure incontact flag have highest precedence
      if (
        (!nextInContact && inContact) ||
        (distance < nextDistance && ((nextInContact && inContact) || !nextInContact))
      ) {
        nextDistance = distance;
        nextObject = object;
        nextInContact = inContact;
      }
    }
    return nextObject;
  }

  /**
   * Get next navigavle link depending on direction - clockwise or conter clockwise.
   * The decision is made based on location of nodes centers rather than link paths or link angles.
   */
  function getNextNavigableLink(
    direction: string,
    listOfNodes: DiagramNode<K1>[],
    currentLink?: DiagramLink<K2, K1>,
    listOfLinks?: DiagramLink<K2, K1>[]
  ) {
    if (!listOfLinks) return currentLink;

    if (!currentLink) return listOfLinks[0];
    const keyboardFocusNode = currentKeyboardFocusNode.current;
    const nodeId = keyboardFocusNode ? keyboardFocusNode : currentLink.startNode;
    const node = getNodeDetailFromInfo({ id: nodeId, isNode: true }, listOfNodes);

    if (!node) return currentLink;
    const adjLinks = getNavigableLinksForNodeId(node.id, listOfLinks);
    listOfLinks = adjLinks;
    const linksWithSortingAttributes = addSortingAttributes(
      node,
      listOfLinks,
      listOfNodes,
      nodeBoundsById
    );
    linksWithSortingAttributes.sort(getLinkComparator<K2>());
    //clockwise direction
    const bForward = direction == 'down' ? true : false;
    let index = 0;
    for (let i = 0; i < linksWithSortingAttributes.length; i++) {
      const link = linksWithSortingAttributes[i];
      if (link.id === currentLink.id) {
        if (bForward) index = i == linksWithSortingAttributes.length - 1 ? 0 : i + 1;
        else index = i == 0 ? linksWithSortingAttributes.length - 1 : i - 1;
        break;
      }
    }
    return linksWithSortingAttributes[index];
  }

  /**
   * Returns the link detail when a node to link navigation occurs
   */
  function navigateFromNodeToLink(
    listOfLinks: DiagramLink<K2, K1>[],
    event: KeyboardEvent,
    node?: DiagramNode<K1>
  ) {
    if (!node) return undefined;
    const adjLinks = getNavigableLinksForNodeId(node.id, listOfLinks);
    if (adjLinks.length < 1) return undefined;
    let link = adjLinks[0];
    const nodeBB = nodeBoundsById.get(node.id)!;
    let object, currNode, currNodeBB, currNodeBBCenterX;
    const nodeCenterX = nodeBB.x + nodeBB.w / 2;
    currentKeyboardFocusNode.current = node.id;
    for (let i = 0; i < adjLinks.length; i++) {
      object = adjLinks[i];
      currNode = object.startNode === node.id ? object.endNode : object.startNode;
      currNodeBB = nodeBoundsById.get(currNode);
      currNodeBBCenterX = currNodeBB!.x + currNodeBB!.w / 2;
      if (
        // equivalent of ≤ and ≥
        (event.altKey && event.code === 'Comma' && currNodeBBCenterX <= nodeCenterX) ||
        (event.altKey && event.code === 'Period' && currNodeBBCenterX >= nodeCenterX)
      ) {
        link = object;
        break;
      }
    }
    return { id: link.id, isNode: false };
  }

  /**
   * Returns the node detail when a link to node navigation occurs
   */
  function navigateFromLinkToNode(linkId: K2, event: KeyboardEvent) {
    const key = event.key;
    const linkData = linkIdToDataMap.get(linkId)!;
    const startNodeBB = nodeBoundsById.get(linkData.startNode);
    const endNodeBB = nodeBoundsById.get(linkData.endNode);
    if (!startNodeBB || !endNodeBB) return undefined;
    const startNodeCenterX = startNodeBB.x + startNodeBB.w / 2;
    const endNodeCenterX = endNodeBB.x + endNodeBB.w / 2;
    const newNode =
      (endNodeCenterX > startNodeCenterX && key === 'ArrowRight') ||
      (endNodeCenterX < startNodeCenterX && key === 'ArrowLeft')
        ? linkData.endNode
        : linkData.startNode;
    return { id: newNode, isNode: true };
  }

  return {
    isNode: (itemInfo: ItemInfo<K1, K2>): boolean => {
      let node, link;
      for (let i = 0; i < nodes.length; i++) {
        node = nodes[i];
        link = links[i];
        if (itemInfo.id === node.id) {
          return true;
        }
        if (itemInfo.id === link.id) {
          return true;
        }
      }
      return false;
    },
    getNextNavigableNode,
    getNextNavigableLink,
    navigateFromNodeToLink,
    navigateFromLinkToNode
  };
}
